import { memo, useMemo } from 'react';

import { Link } from '@tanstack/react-router';
import { twMerge } from 'tailwind-merge';

import { Avatar, AvatarFallback, AvatarImage } from '@/_components';
import { PhoneNumbersLinker, SortableList } from '@/_containers';
import type { TeamMemberQueryLinkerWithContactInfoSchema } from '@/_schemas/team-members/team-members-query-linker-with-contact-info-schema';
import { nameResolver } from '@/_shared';

type ItemProps = {
  item: TeamMemberQueryLinkerWithContactInfoSchema;
  removeItemHandler: (item: TeamMemberQueryLinkerWithContactInfoSchema) => void;
  isEditable?: boolean;
  className?: string;
  showPrimaryContactAction?: boolean;
  isPrimary?: boolean;
  hasPrimary?: boolean;
  sortable?: boolean;
  setContactType?: (item: TeamMemberQueryLinkerWithContactInfoSchema, isPrimary: boolean) => void;
};

function ItemWithPhoneNumberBase({
  item,
  isEditable = false,
  removeItemHandler,
  className,
  showPrimaryContactAction = false,
  isPrimary = false,
  hasPrimary = false,
  sortable = false,
  setContactType,
}: ItemProps) {
  const BaseItem = useMemo(
    () => (
      <section
        className={twMerge(
          'border-c-border-muted text-accent flex min-h-11 flex-1 flex-col flex-nowrap items-start justify-between gap-1.5 border-b py-2 sm:flex-row dark:text-gray-400',
          className
        )}
      >
        <div className="w-full sm:w-1/3">
          <Link
            to="/team-members/$id"
            params={{ id: `${item.id}` }}
            state={{ naturalBack: true }}
            className="group flex items-center gap-1.5"
          >
            <Avatar>
              <AvatarImage src={item.profile_image?.[0]?.url} />
              <AvatarFallback name={nameResolver(item.first_name, item.last_name)} />
            </Avatar>

            <span className="decoration-primary text-sm decoration-2 group-hover:underline">
              {nameResolver(item.first_name, item.last_name)}
            </span>
          </Link>
        </div>

        <div className="w-full sm:w-1/3">
          <PhoneNumbersLinker
            phone_numbers={item.phone_numbers}
            isEditable={isEditable}
            removeItemHandler={() => removeItemHandler(item)}
            showPrimaryContactAction={showPrimaryContactAction}
            isPrimary={isPrimary}
            hasPrimary={hasPrimary}
            setContactType={() => setContactType?.(item, isPrimary)}
          />
        </div>
      </section>
    ),
    [className, hasPrimary, isEditable, isPrimary, item, removeItemHandler, setContactType, showPrimaryContactAction]
  );

  return sortable ? (
    <SortableList.Item key={item.id} id={item.id} className="items-center!">
      {isEditable && <SortableList.DragHandle className="mr-1! mb-0.5!" />}
      {BaseItem}
    </SortableList.Item>
  ) : (
    BaseItem
  );
}

export const ItemWithPhoneNumber = memo(ItemWithPhoneNumberBase);
